'use client';

import { useTranslations } from 'next-intl';
import { Eye, Trash2, Package, Lock, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { PermissionGate } from '@/features/auth';
import type { Box, BoxStatus } from '../types';

interface PackingCardProps {
  box: Box;
  onDelete: (box: Box) => void;
  statusStyles: Record<BoxStatus, string>;
}

export function PackingCard({ box, onDelete, statusStyles }: PackingCardProps) {
  const t = useTranslations('packing');

  const statusLabels: Record<BoxStatus, string> = {
    available: t('statusAvailable'),
    packing: t('statusPacking'),
    ready_for_shipping: t('statusReadyForShipping'),
    in_shipping: t('statusInShipping'),
    in_transit: t('statusInTransit'),
    received: t('statusReceived')
  };

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: box_number + status badge */}
        <div className='mb-4 flex items-start justify-between'>
          <div className='flex items-center gap-3'>
            <div className='bg-primary/10 flex h-10 w-10 items-center justify-center rounded-md'>
              <Package className='text-primary h-5 w-5' />
            </div>
            <div>
              <Link
                href={`/dashboard/packing/${box.id}`}
                className='text-primary font-mono font-bold hover:underline'
              >
                {box.box_number}
              </Link>
              {box.box_type && (
                <p className='text-muted-foreground text-xs'>
                  {box.box_type.name}
                </p>
              )}
            </div>
          </div>
          <Badge
            variant='secondary'
            className={cn('shrink-0 text-xs', statusStyles[box.status])}
          >
            {statusLabels[box.status] ?? box.status}
          </Badge>
        </div>

        {/* Middle: capacity, seals, creator */}
        <div className='text-muted-foreground mb-4 space-y-1.5 text-sm'>
          <p>
            {t('assignedContainers')}:{' '}
            <span className='text-foreground font-medium'>
              {box.registrations_count ?? 0}/{box.box_type?.max_capacity ?? 0}
            </span>
          </p>
          {(box.security_seal_1 || box.security_seal_2) && (
            <div className='flex items-center gap-1.5'>
              <Lock className='h-3.5 w-3.5' />
              <span>
                {[box.security_seal_1, box.security_seal_2]
                  .filter(Boolean)
                  .join(' / ')}
              </span>
            </div>
          )}
          {box.creator && (
            <div className='flex items-center gap-1.5'>
              <User className='h-3.5 w-3.5' />
              <span>{box.creator.name}</span>
            </div>
          )}
        </div>

        {/* Bottom: action buttons */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <Button variant='ghost' size='icon' className='h-8 w-8' asChild>
            <Link href={`/dashboard/packing/${box.id}`}>
              <Eye className='h-4 w-4' />
            </Link>
          </Button>
          {box.status !== 'received' && (
            <PermissionGate permissions={['boxes.delete']}>
              <Button
                variant='ghost'
                size='icon'
                className='text-destructive h-8 w-8'
                onClick={() => onDelete(box)}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </PermissionGate>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
